import React, { useMemo, useState } from "react";
import { Box, Button, Grid, Paper, Typography, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip } from "@mui/material";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import EChartBase from "../../../components/charts/EChartBase";
import { ECHARTS_COLORS } from "../../../constants/echartsConfig";
import { formatCompactNumber, formatExposure, formatShortExposure } from "../../../utils/formatters";
import { LIMIT_METRICS, getUtilizationStatus } from "../utils/riskLimitsCalcs";

const STATUS_COLOR = { good: "success.main", warning: "warning.main", critical: "error.main" };
const STATUS_TRACK = { good: "#d3f0e4", warning: "#fbe7cb", critical: "#fad2d2" };
const STATUS_ICON = { warning: WarningRoundedIcon, critical: ErrorRoundedIcon };

// NMV needs its own colour: ECHARTS_COLORS.netPnl is the same blue as GMV (neutral).
const NMV_COLOR = "#6B5B95";
const NUMERATOR_FIELD_BY_KEY = { lmv: "lmv", smv: "smv", gmv: "gross_exposure" };

const MetricToggle = ({ value, onChange }) => (
  <ToggleButtonGroup value={value} exclusive size="small" onChange={(e, next) => next && onChange(next)}>
    {LIMIT_METRICS.map((m) => (
      <ToggleButton key={m.key} value={m.key} sx={{ px: 1.5, py: 0.25, fontSize: 12 }}>
        {m.key.toUpperCase()}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

/**
 * Exposure Analytics tab for the strategy-type drill-down: the same "exposure
 * share" donut + "utilization ranking" pattern as the top-level Strategy
 * Type Comparison, but one level down — by strategy within the strategy
 * type the user clicked into, so it answers "which strategy inside this
 * type is actually driving the type's risk?" Plus a toggleable LMV/SMV/GMV
 * vs. Risk Capital bar comparing the type's totals against its cap.
 */
const UTILIZATION_RANKING_PAGE_SIZE = 5;

export default function StrategyTypeRiskAnalytics({ strategiesData = [], riskCapital, limitCaps = {}, strategyTypeName }) {
  const [exposureMetricKey, setExposureMetricKey] = useState("gmv");
  const [utilizationMetricKey, setUtilizationMetricKey] = useState("gmv");
  const [visibleMetrics, setVisibleMetrics] = useState(new Set(["lmv", "smv", "gmv", "nmv", "risk_capital"]));
  const [showAllUtilization, setShowAllUtilization] = useState(false);

  const exposureMetric = LIMIT_METRICS.find((m) => m.key === exposureMetricKey);
  const utilizationMetric = LIMIT_METRICS.find((m) => m.key === utilizationMetricKey);

  const totals = useMemo(
    () =>
      strategiesData.reduce(
        (acc, row) => ({
          lmv: acc.lmv + (row.lmv || 0),
          smv: acc.smv + (row.smv || 0),
          gross_exposure: acc.gross_exposure + (row.gross_exposure || 0),
        }),
        { lmv: 0, smv: 0, gross_exposure: 0 },
      ),
    [strategiesData],
  );

  // Treemap, not a donut: a strategy type can carry dozens of individual
  // strategies (deals/positions) where even the largest is a few percent of
  // total exposure — a pie/donut turns into an unreadable ring of slivers at
  // that count. Area-proportional tiles stay legible down to a handful of
  // pixels, so the long tail is still visible instead of getting crushed
  // into "Other."
  const treemapOption = useMemo(() => {
    const field = NUMERATOR_FIELD_BY_KEY[exposureMetricKey];
    const data = strategiesData
      .map((row) => ({ name: row.strategy_name, value: Math.abs(row[field] || 0) }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
    const total = data.reduce((sum, d) => sum + d.value, 0);

    return {
      color: ECHARTS_COLORS.primary,
      tooltip: {
        formatter: (p) => {
          const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : "0.0";
          return `${p.name}<br/>${formatExposure(p.value)} (${pct}%)`;
        },
      },
      series: [
        {
          name: exposureMetric.label,
          type: "treemap",
          roam: false,
          nodeClick: false,
          breadcrumb: { show: false },
          label: { show: true, fontSize: 11, color: "#fff", overflow: "truncate" },
          upperLabel: { show: false },
          itemStyle: { borderColor: "#fff", borderWidth: 2, gapWidth: 2 },
          data,
        },
      ],
    };
  }, [strategiesData, exposureMetricKey, exposureMetric]);

  const utilizationRows = useMemo(() => {
    const field = NUMERATOR_FIELD_BY_KEY[utilizationMetricKey];
    const cap = Number(limitCaps[utilizationMetric.capField]);
    if (!riskCapital || !Number.isFinite(cap) || cap <= 0) return { capped: [], uncapped: strategiesData.map((r) => r.strategy_name) };

    const capped = strategiesData
      .map((row) => {
        const exposure = Math.abs(row[field] || 0);
        const currentPct = (exposure / riskCapital) * 100;
        return { name: row.strategy_name, exposure, percentOfCap: (currentPct / cap) * 100 };
      })
      .filter((r) => r.exposure > 0)
      .sort((a, b) => b.percentOfCap - a.percentOfCap);

    return { capped, uncapped: [] };
  }, [strategiesData, utilizationMetricKey, utilizationMetric, riskCapital, limitCaps]);

  const barOption = useMemo(() => {
    const metrics = [
      { key: "lmv", label: "LMV", value: totals.lmv, color: ECHARTS_COLORS.lmv },
      { key: "smv", label: "SMV", value: totals.smv, color: ECHARTS_COLORS.smv },
      { key: "gmv", label: "GMV", value: totals.lmv + totals.smv, color: ECHARTS_COLORS.neutral },
      // SMV totals are positive magnitudes, so NMV = LMV - SMV (negative for a net-short type).
      { key: "nmv", label: "NMV", value: totals.lmv - totals.smv, color: NMV_COLOR },
      { key: "risk_capital", label: "Risk Capital", value: riskCapital || 0, color: ECHARTS_COLORS.highlight },
    ].filter((m) => visibleMetrics.has(m.key));

    return {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, formatter: (params) => params.map((p) => `${p.seriesName}: ${p.seriesName === "SMV" ? formatShortExposure(p.value) : formatExposure(p.value)}`).join("<br/>") },
      legend: { top: 0, textStyle: { fontSize: 11 } },
      grid: { left: 70, right: 20, top: 40, bottom: 30 },
      xAxis: { type: "category", data: [strategyTypeName || "Selected type"], axisLabel: { fontSize: 11 } },
      yAxis: { type: "value", axisLabel: { formatter: (v) => formatCompactNumber(v) } },
      series: metrics.map((m) => ({
        name: m.label,
        type: "bar",
        barGap: "20%",
        itemStyle: { color: m.color, borderRadius: [4, 4, 0, 0] },
        data: [m.value],
      })),
    };
  }, [totals, riskCapital, strategyTypeName, visibleMetrics]);

  const BAR_TOGGLE_OPTIONS = [
    { key: "lmv", label: "LMV" },
    { key: "smv", label: "SMV" },
    { key: "gmv", label: "GMV" },
    { key: "nmv", label: "NMV" },
    { key: "risk_capital", label: "Risk Capital" },
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1">Exposure by Strategy</Typography>
              <MetricToggle value={exposureMetricKey} onChange={setExposureMetricKey} />
            </Box>
            <EChartBase option={treemapOption} height={320} emptyMessage="No exposure data for this strategy type." />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography variant="subtitle1">Strategy Utilization Ranking</Typography>
              <MetricToggle value={utilizationMetricKey} onChange={setUtilizationMetricKey} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Each strategy&apos;s share of the type&apos;s {utilizationMetric.label} cap — which strategy is driving this
              type&apos;s risk.
            </Typography>
            {utilizationRows.capped.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No {utilizationMetric.label} cap configured for this strategy type yet.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, mt: 1.5 }}>
                  {(showAllUtilization ? utilizationRows.capped : utilizationRows.capped.slice(0, UTILIZATION_RANKING_PAGE_SIZE)).map((item) => {
                    const status = getUtilizationStatus(item.percentOfCap);
                    const StatusIcon = STATUS_ICON[status];
                    const fillPct = Math.min(Math.max(item.percentOfCap, 0), 100);
                    return (
                      <MuiTooltip key={item.name} arrow title={`${utilizationMetric.label} ${utilizationMetric.key === "smv" ? formatShortExposure(item.exposure) : formatExposure(item.exposure)} — ${item.percentOfCap.toFixed(1)}% of the type's cap`}>
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                              {StatusIcon && <StatusIcon sx={{ fontSize: 15, color: STATUS_COLOR[status] }} />}
                              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                {item.name}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: status === "critical" ? "error.main" : "text.secondary" }}>
                              {item.percentOfCap.toFixed(1)}% of cap
                            </Typography>
                          </Box>
                          <Box sx={{ position: "relative", height: 10, backgroundColor: STATUS_TRACK[status], borderRadius: 999, overflow: "hidden" }}>
                            <Box sx={{ height: "100%", width: `${fillPct}%`, backgroundColor: STATUS_COLOR[status], borderRadius: 999, transition: "width 0.4s ease" }} />
                          </Box>
                        </Box>
                      </MuiTooltip>
                    );
                  })}
                </Box>
                {utilizationRows.capped.length > UTILIZATION_RANKING_PAGE_SIZE && (
                  <Button size="small" onClick={() => setShowAllUtilization((v) => !v)} sx={{ mt: 1.5 }}>
                    {showAllUtilization ? "Show top 5 only" : `View all ${utilizationRows.capped.length}`}
                  </Button>
                )}
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, flexWrap: "wrap", gap: 1 }}>
              <Typography variant="subtitle1">Risk Capital vs. Exposure</Typography>
              <ToggleButtonGroup size="small" value={[...visibleMetrics]} onChange={(e, next) => setVisibleMetrics(new Set(next))}>
                {BAR_TOGGLE_OPTIONS.map((opt) => (
                  <ToggleButton key={opt.key} value={opt.key} sx={{ px: 1.5, py: 0.25, fontSize: 12 }}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Typography variant="caption" color="text.secondary">
              This strategy type&apos;s total LMV/SMV/GMV/NMV against its configured Risk Capital — toggle metrics to compare.
            </Typography>
            <EChartBase option={barOption} height={260} emptyMessage="No metrics selected." />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
